import {
  buildGoogleMapsEmbedUrl,
  createDefaultMapEditValues,
  describeMap,
  getMapValidationMessage,
  mapConfigToEditValues,
  mapConfigToSerializable,
  mapEditValuesToConfig,
  normalizeMapConfig,
  stringifyMapConfig,
  type MapConfig,
  type MapEditRequest,
  type MapEditValues,
} from '#components/document-viewer/map/map-config';

export const NP_MAP_TAG = 'np-map';
export const NP_MAP_OPEN_TAG = `<${NP_MAP_TAG}>`;
export const NP_MAP_CLOSE_TAG = `</${NP_MAP_TAG}>`;

export type MapBlockData = MapConfig;
export type MarkdownMapEditValues = MapEditValues;
export type MarkdownMapEditRequest = MapEditRequest;

export {
  createDefaultMapEditValues as createDefaultMarkdownMapEditValues,
  mapConfigToEditValues as mapBlockDataToEditValues,
  mapEditValuesToConfig as markdownMapEditValuesToBlockData,
  normalizeMapConfig as normalizeMapBlockData,
  stringifyMapConfig as stringifyMarkdownMapConfig,
  describeMap as describeMarkdownMap,
  getMapValidationMessage as getMarkdownMapValidationMessage,
  buildGoogleMapsEmbedUrl,
};

export type {
  MapMode,
  MapType,
  MapTravelMode,
  MapUnits,
  MapCoordinates,
} from '#components/document-viewer/map/map-config';

export interface ParsedMarkdownMapConfig {
  data: MapConfig;
  rawConfig: string;
  parseError: string | null;
}

function normalizeRawConfig(value: string): string {
  return value.replace(/\r\n/g, '\n').trim();
}

function minifyJsonConfig(value: unknown): string {
  return JSON.stringify(value);
}

export function parseMarkdownMapConfig(
  rawConfig: string,
): ParsedMarkdownMapConfig {
  const normalizedRaw = normalizeRawConfig(rawConfig);

  if (!normalizedRaw) {
    return {
      data: normalizeMapConfig({}),
      rawConfig: normalizedRaw,
      parseError: null,
    };
  }

  try {
    return {
      data: normalizeMapConfig(JSON.parse(normalizedRaw) as unknown),
      rawConfig: normalizedRaw,
      parseError: null,
    };
  } catch (error) {
    return {
      data: normalizeMapConfig({}),
      rawConfig: normalizedRaw,
      parseError:
        error instanceof Error ? error.message : 'Could not parse map JSON',
    };
  }
}

export function serializeMarkdownMapInnerConfig(params: {
  data: MapConfig;
  rawConfig?: string;
  parseError?: string | null;
}): string {
  const normalizedRaw = normalizeRawConfig(params.rawConfig ?? '');
  if (params.parseError && normalizedRaw) {
    return normalizedRaw;
  }

  if (normalizedRaw) {
    try {
      return minifyJsonConfig(JSON.parse(normalizedRaw) as unknown);
    } catch {
      return normalizedRaw;
    }
  }

  return minifyJsonConfig(mapConfigToSerializable(params.data));
}

export function serializeMarkdownMapBlock(params: {
  data: MapConfig;
  rawConfig?: string;
  parseError?: string | null;
}): string {
  return `${NP_MAP_OPEN_TAG}${serializeMarkdownMapInnerConfig(params)}${NP_MAP_CLOSE_TAG}`;
}

export function createDefaultMarkdownMapValue(
  initialValues?: Partial<MapEditValues>,
): string {
  return serializeMarkdownMapBlock({
    data: mapEditValuesToConfig({
      ...createDefaultMapEditValues(),
      ...initialValues,
    }),
  });
}
